/**
 * **형태가 다른 가구** (2026-09-15, Chris: 「디자인은 다 동일하고 색상놀이네 — 유지하고 좀 더 만들어도」).
 * variants.ts 는 같은 틀의 치수·색 프리셋이고, 여기는 **실루엣이 다른 새 빌더**다. 종류마다 두셋:
 * 탁자(X다리·좌탁·철제 카페·드럼) · 의자(스툴·팔걸이·접이식·벤치) · 소파(안락의자·데이베드) ·
 * 선반(사다리·큐브·벽선반) · 침대(플랫폼·철제) · 책상(트레슬·콘솔) · 협탁(스툴형) · 옷걸이 봉.
 * 전부 닫힌 상자·원기둥 조합이라 카탈로그 검사(구멍·뒤집힘)를 그대로 통과한다. 원점 바닥 중앙, 정면 −Z.
 */
import * as THREE from 'three';
import { bevelBox, lathe, type MeshWriter, type Slot } from './kernel';
import type { Piece } from './pieces';

const WOODS: Slot[] = ['walnut', 'oak', 'ebony', 'painted'];
const woodOf = (d: Record<string, number>, def: Slot): Slot => WOODS[d.wood ?? -1] ?? def;
const box = (w: MeshWriter, slot: Slot, sx: number, sy: number, sz: number, x: number, y: number, z: number, bevel = 0.004, tile = 0.6) => {
  const g = bevelBox(sx, sy, sz, bevel); g.translate(x, y, z); w.add(g, slot, tile);
};
const cyl = (w: MeshWriter, slot: Slot, r: number, h: number, x: number, y: number, z: number, seg = 14, tile = 0.4) => {
  const g = new THREE.CylinderGeometry(r, r, h, seg); g.translate(x, y + h / 2, z); w.add(g, slot, tile);
};
/** 기울어진 막대 — 두 점을 잇는 각재 */
function bar(w: MeshWriter, slot: Slot, a: THREE.Vector3, b: THREE.Vector3, t: number) {
  const len = a.distanceTo(b);
  const g = bevelBox(t, len, t, 0.003);
  const mid = a.clone().add(b).multiplyScalar(0.5);
  const dir = b.clone().sub(a).normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  g.applyQuaternion(q); g.translate(mid.x, mid.y, mid.z);
  w.add(g, slot, 0.5);
}

export const SHAPE2_PIECES: Piece[] = [
  // ---------- 탁자 ----------
  {
    id: 'table_xleg', name: 'X다리 탁자', collection: 'manor', category: '거실', nofront: true,
    note: '두꺼운 널 상판에 X자로 교차한 각재 다리 둘, 아래 가로대. 농가 식탁.',
    dims: { w: 1.5, d: 0.8, h: 0.74, topT: 0.05, legT: 0.06, wood: -1 },
    /* 탁자를 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '탁자', slide: [0, 0, -0.15] }],
    build(w, d) {
      w.movable('탁자', () => {
      const W = woodOf(d, 'oak');
      w.part('상판'); box(w, W, d.w, d.topT, d.d, 0, d.h - d.topT / 2, 0, 0.006);
      w.part('다리');
      const ex = d.w / 2 - 0.2, hz = d.d / 2 - 0.05, top = d.h - d.topT;
      for (const sx of [-1, 1]) {
        bar(w, W, new THREE.Vector3(sx * ex, 0.02, -hz), new THREE.Vector3(sx * ex, top, hz), d.legT);
        bar(w, W, new THREE.Vector3(sx * ex, 0.02, hz), new THREE.Vector3(sx * ex, top, -hz), d.legT);
        box(w, W, d.legT, 0.03, d.d - 0.04, sx * ex, 0.015, 0);   // 발판
      }
      box(w, W, d.w - 0.5, 0.05, 0.05, 0, 0.36, 0);   // 가로대
      });
    },
  },
  {
    id: 'table_zataku', name: '좌탁', collection: 'manor', category: '거실', nofront: true,
    note: '바닥에 앉아 쓰는 낮은 상. 두꺼운 상판에 짧은 각목 다리 넷, 다리 사이 낮은 가로대.',
    dims: { w: 1.2, d: 0.7, h: 0.34, topT: 0.04, legT: 0.07, wood: -1 },
    /* 좌탁을 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '좌탁', slide: [0, 0, -0.12] }],
    build(w, d) {
      w.movable('좌탁', () => {
      const W = woodOf(d, 'walnut');
      w.part('상판'); box(w, W, d.w, d.topT, d.d, 0, d.h - d.topT / 2, 0, 0.008);
      w.part('다리');
      const ix = d.w / 2 - 0.08, iz = d.d / 2 - 0.08;
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(w, W, d.legT, d.h - d.topT, d.legT, sx * ix, (d.h - d.topT) / 2, sz * iz);
      for (const sx of [-1, 1]) box(w, W, 0.03, 0.04, d.d - 0.16 - d.legT, sx * ix, 0.08, 0);
      });
    },
  },
  {
    id: 'table_cafe', name: '철제 카페 테이블', collection: 'manor', category: '거실', nofront: true,
    /* 사건 메모: 차유리 취향. */
    note: '둥근 나무 상판에 강관 다리 셋이 밖으로 벌어지고 고무 발이 달렸다.',
    dims: { r: 0.4, h: 0.72, topT: 0.035, legT: 0.024, wood: -1 },
    /* 테이블을 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '테이블', slide: [0, 0, -0.15] }],
    build(w, d) {
      w.movable('테이블', () => {
      const W = woodOf(d, 'oak');
      w.part('상판'); const top = new THREE.CylinderGeometry(d.r, d.r, d.topT, 40); top.translate(0, d.h - d.topT / 2, 0); w.add(top, W, 0.8);
      const hub = new THREE.CylinderGeometry(0.07, 0.07, 0.03, 20); hub.translate(0, d.h - d.topT - 0.015, 0); w.add(hub, 'steel', 0.3);
      w.part('다리');
      const y1 = d.h - d.topT - 0.02;
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
        const foot = new THREE.Vector3(Math.sin(a) * d.r * 0.85, 0.012, Math.cos(a) * d.r * 0.85);
        const head = new THREE.Vector3(Math.sin(a) * 0.05, y1, Math.cos(a) * 0.05);
        bar(w, 'steel', foot, head, d.legT);
        const cap = new THREE.CylinderGeometry(0.022, 0.022, 0.014, 12); cap.translate(foot.x, 0.007, foot.z); w.add(cap, 'rubber', 0.3);
      }
      });
    },
  },
  {
    id: 'table_drum', name: '드럼 탁자', collection: 'manor', category: '거실', nofront: true,
    note: '통나무 토막처럼 굵은 원통 몸통 위에 넓은 원판. 아래가 꽉 차서 묵직하다.',
    dims: { r: 0.45, h: 0.5, topT: 0.04, coreR: 0.3, wood: -1 },
    /* 탁자를 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '탁자', slide: [0, 0, -0.12] }],
    build(w, d) {
      w.movable('탁자', () => {
      const W = woodOf(d, 'walnut');
      w.part('상판'); const top = new THREE.CylinderGeometry(d.r, d.r, d.topT, 40); top.translate(0, d.h - d.topT / 2, 0); w.add(top, W, 0.8);
      w.part('몸통'); const core = lathe([[d.coreR * 0.9, 0], [d.coreR, 0.04], [d.coreR, d.h - d.topT - 0.03], [d.coreR * 0.92, d.h - d.topT]], 28); w.add(core, W, 0.5);
      });
    },
  },
  // ---------- 의자 ----------
  {
    id: 'chair_stool', name: '둥근 스툴', collection: 'manor', category: '주방', nofront: true,
    note: '두툼한 둥근 좌판에 밖으로 벌어진 다리 셋과 가로대. 주방 조리대 옆.',
    dims: { r: 0.18, h: 0.45, seatT: 0.045, legT: 0.034, wood: -1 },
    /* 스툴을 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '스툴', slide: [0, 0, -0.12] }],
    build(w, d) {
      w.movable('스툴', () => {
      const W = woodOf(d, 'oak');
      w.part('좌판'); const seat = new THREE.CylinderGeometry(d.r, d.r * 0.94, d.seatT, 28); seat.translate(0, d.h - d.seatT / 2, 0); w.add(seat, W, 0.6);
      w.part('다리');
      const top = d.h - d.seatT - 0.005, feet: THREE.Vector3[] = [];
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const foot = new THREE.Vector3(Math.sin(a) * d.r * 1.05, 0, Math.cos(a) * d.r * 1.05);     // 바닥에서 밖으로
        const head = new THREE.Vector3(Math.sin(a) * d.r * 0.55, top, Math.cos(a) * d.r * 0.55);   // 좌판 밑 안쪽
        bar(w, W, foot, head, d.legT); feet.push(foot);
      }
      w.part('가로대');
      for (let i = 0; i < 3; i++) {
        const p = feet[i].clone().lerp(new THREE.Vector3(feet[i].x * 0.55, 0, feet[i].z * 0.55), 0.36).setY(0.16);
        const q = feet[(i + 1) % 3].clone().lerp(new THREE.Vector3(feet[(i + 1) % 3].x * 0.55, 0, feet[(i + 1) % 3].z * 0.55), 0.36).setY(0.16);
        bar(w, W, p, q, 0.022);
      }
      });
    },
  },
  {
    id: 'chair_arm', name: '팔걸이 의자', collection: 'manor', category: '거실', nofront: true,
    note: '각진 나무 틀에 천 좌판·등판, 양쪽 팔걸이. 서재나 거실 구석에 하나.',
    dims: { w: 0.6, d: 0.6, seatH: 0.42, backH: 0.85, armH: 0.64, legT: 0.045, wood: -1 },
    /* 의자를 끌어 빼 본다 (2026-09-16) */
    openable: [{ part: '의자', slide: [0, 0, -0.15] }],
    build(w, d) {
      w.movable('의자', () => {
      const W = woodOf(d, 'walnut');
      const hw = d.w / 2 - d.legT / 2, hd = d.d / 2 - d.legT / 2;
      w.part('다리');
      for (const sx of [-1, 1]) { box(w, W, d.legT, d.backH, d.legT, sx * hw, d.backH / 2, hd); box(w, W, d.legT, d.armH, d.legT, sx * hw, d.armH / 2, -hd); }
      w.part('팔걸이'); for (const sx of [-1, 1]) box(w, W, d.legT + 0.02, 0.03, d.d, sx * hw, d.armH + 0.015, 0);
      w.part('좌판'); box(w, W, d.w - d.legT * 2, 0.04, d.d - d.legT * 2, 0, d.seatH - 0.02, 0);
      const cushion = bevelBox(d.w - d.legT * 2 - 0.02, 0.07, d.d - d.legT * 2 - 0.02, 0.02, 2); cushion.translate(0, d.seatH + 0.035, 0); w.add(cushion, 'fabric', 0.6);
      w.part('등판'); const back = bevelBox(d.w - d.legT * 2, 0.34, 0.05, 0.015, 2); back.translate(0, d.seatH + 0.28, hd - 0.01); w.add(back, 'fabric', 0.6);
      box(w, W, d.w - d.legT * 2, 0.04, d.legT, 0, d.backH - 0.02, hd);
      });
    },
  },
  {
    id: 'chair_folding', name: '접이식 철제 의자', collection: 'manor', category: '창고', nofront: true,
    note: '강관 틀에 얇은 철판 좌판·등판. 회의실이나 창고에 쌓아 두는 것.',
    dims: { w: 0.45, d: 0.48, seatH: 0.45, backH: 0.82, tube: 0.02 },
    /* 접혀 있는 좌판을 E 로 펼친다 — 기본 자세는 접힘(세워 둔 접의자) (2026-09-16) */
    openable: [{ part: '좌판', swing: -1.35, axis: 'x', pivot: [0, 0.45, 0.19] }],
    build(w, d) {
      const hw = d.w / 2 - d.tube / 2;
      w.part('틀');
      for (const sx of [-1, 1]) {
        bar(w, 'steel', new THREE.Vector3(sx * hw, 0.01, -d.d / 2 + 0.03), new THREE.Vector3(sx * hw, d.backH, d.d / 2 - 0.16), d.tube);   // 뒷다리→등받이
        bar(w, 'steel', new THREE.Vector3(sx * hw, 0.01, d.d / 2 - 0.03), new THREE.Vector3(sx * hw, d.seatH, -d.d / 2 + 0.05), d.tube);   // 앞다리(교차)
      }
      box(w, 'steel', d.w, d.tube, d.tube, 0, d.backH, d.d / 2 - 0.16);
      w.part('좌판'); w.movable('좌판', () => {
        const seat = bevelBox(d.w - 0.02, 0.018, d.d - 0.1, 0.004);
        seat.translate(0, d.seatH, 0);
        // 접힌 자세로 굽힌다 — 뒤 모서리 경첩(pivot)을 축으로 앞끝이 위로 올라간 상태가 기본
        seat.translate(0, -d.seatH, -0.19); seat.rotateX(1.35); seat.translate(0, d.seatH, 0.19);
        w.add(seat, 'painted', 0.5);
      });
      w.part('등판'); const back = bevelBox(d.w - 0.02, 0.2, 0.014, 0.004); back.translate(0, d.backH - 0.14, d.d / 2 - 0.15); w.add(back, 'painted', 0.5);
    },
  },
  {
    id: 'bench', name: '긴 벤치', collection: 'manor', category: '주방', nofront: true,
    note: '두꺼운 널 하나에 널판 다리 둘. 식탁 한쪽에 의자 대신.',
    dims: { w: 1.3, d: 0.32, h: 0.44, topT: 0.04, wood: -1 },
    /* 벤치를 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '벤치', slide: [0, 0, -0.15] }],
    build(w, d) {
      w.movable('벤치', () => {
      const W = woodOf(d, 'oak');
      w.part('좌판'); box(w, W, d.w, d.topT, d.d, 0, d.h - d.topT / 2, 0, 0.006);
      w.part('다리'); for (const sx of [-1, 1]) box(w, W, 0.04, d.h - d.topT, d.d - 0.06, sx * (d.w / 2 - 0.15), (d.h - d.topT) / 2, 0);
      box(w, W, d.w - 0.4, 0.06, 0.03, 0, d.h - d.topT - 0.05, 0);
      });
    },
  },
  // ---------- 소파 ----------
  {
    id: 'armchair', name: '안락의자', collection: 'manor', category: '거실', nofront: true,
    note: '통통한 천 안락의자 하나. 낮은 팔걸이, 두꺼운 방석, 짧은 나무 다리.',
    dims: { w: 0.85, d: 0.85, h: 0.8, seat: 0.42, arm: 0.2, cloth: -1 },
    /* 방석을 들어 올려 밑을 본다 (2026-09-16) */
    openable: [{ part: '방석', slide: [0, 0, -0.22] }],
    build(w, d) {
      const F = (['fabric', 'leather', 'redcloth'] as const)[d.cloth ?? -1] ?? 'fabric';
      w.part('몸통'); const base = bevelBox(d.w, d.seat - 0.1, d.d, 0.03, 2); base.translate(0, 0.1 + (d.seat - 0.1) / 2, 0); w.add(base, F, 0.6);
      w.part('팔걸이'); for (const sx of [-1, 1]) { const a = bevelBox(d.arm, 0.24, d.d, 0.05, 3); a.translate(sx * (d.w / 2 - d.arm / 2), d.seat + 0.12, 0); w.add(a, F, 0.6); }
      w.part('등받이'); const back = bevelBox(d.w, d.h - d.seat, 0.22, 0.05, 3); back.translate(0, d.seat + (d.h - d.seat) / 2, d.d / 2 - 0.11); w.add(back, F, 0.6);
      w.part('방석'); w.movable('방석', () => { const c = bevelBox(d.w - d.arm * 2 - 0.02, 0.1, d.d - 0.26, 0.03, 2); c.translate(0, d.seat + 0.05, -0.1); w.add(c, F, 0.6); });
      w.part('다리'); for (const sx of [-1, 1]) for (const sz of [-1, 1]) cyl(w, 'walnut', 0.025, 0.1, sx * (d.w / 2 - 0.08), 0, sz * (d.d / 2 - 0.08), 10);
    },
  },
  {
    id: 'daybed', name: '데이베드', collection: 'manor', category: '거실', nofront: true,
    note: '등받이 없는 긴 소파. 낮은 나무 틀에 두꺼운 매트와 베개 둘. 낮잠 자리.',
    dims: { w: 1.9, d: 0.8, h: 0.42, frame: 0.12, wood: -1, cloth: -1 },
    /* 베개를 들어 올려 밑을 본다 (2026-09-16) */
    openable: [{ part: '베개', slide: [0, 0, -0.22] }],
    build(w, d) {
      const W = woodOf(d, 'oak'); const F = (['fabric', 'leather', 'redcloth'] as const)[d.cloth ?? -1] ?? 'fabric';
      w.part('틀'); box(w, W, d.w, d.frame, d.d, 0, 0.12 + d.frame / 2, 0, 0.006);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(w, W, 0.05, 0.12, 0.05, sx * (d.w / 2 - 0.05), 0.06, sz * (d.d / 2 - 0.05));
      w.part('매트'); const m = bevelBox(d.w - 0.04, d.h - 0.12 - d.frame, d.d - 0.04, 0.03, 2); m.translate(0, 0.12 + d.frame + (d.h - 0.12 - d.frame) / 2, 0); w.add(m, F, 0.6);
      w.part('베개'); for (const sx of [-1, 1]) { const drawP = () => { const p = bevelBox(0.45, 0.14, 0.3, 0.04, 3); p.translate(sx * (d.w / 2 - 0.3), d.h + 0.07, 0.15); w.add(p, F, 0.6); }; if (sx === -1) w.movable('베개', drawP); else drawP(); }
    },
  },
  // ---------- 선반 ----------
  {
    id: 'shelf_ladder', name: '사다리 선반', collection: 'manor', category: '서재', nofront: true,
    note: '벽에 기대는 사다리꼴 선반. 위로 갈수록 얕아진다. 아래 칸이 제일 깊다.',
    dims: { w: 0.6, h: 1.8, d: 0.4, shelves: 4, wood: -1 },
    /* 맨 아랫칸에 꽂힌 책을 뽑아 본다 (2026-09-16) */
    openable: [{ part: '책', slide: [0, 0, -0.16] }],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      w.part('기둥');
      for (const sx of [-1, 1]) bar(w, W, new THREE.Vector3(sx * (d.w / 2 - 0.02), 0, -d.d / 2 + 0.02), new THREE.Vector3(sx * (d.w / 2 - 0.02), d.h, d.d / 2 - 0.02), 0.04);
      w.part('선반');
      // 선반 뒷단이 기울어진 기둥에 얹히고 앞으로 뻗는다 — 아래일수록 깊다. 기둥은 앞쪽 바닥에서 뒤쪽 꼭대기로 기운다
      for (let i = 0; i < d.shelves; i++) {
        const y = 0.2 + (i / (d.shelves - 1)) * (d.h - 0.5);
        const zr = -d.d / 2 + 0.02 + (y / d.h) * (d.d - 0.04);   // 그 높이에서 기둥의 z
        const depth = Math.max(0.12, d.d / 2 - zr + 0.02);
        box(w, W, d.w - 0.1, 0.022, depth, 0, y, zr - depth / 2 + 0.03);
        box(w, W, d.w - 0.1, 0.05, 0.018, 0, y + 0.03, zr - depth + 0.04);   // 앞턱
      }
      // 맨 아랫칸(제일 깊다)에 기대 둔 책 — 뽑아 본다
      w.movable('책', () => {
        const bk = bevelBox(0.05, 0.22, 0.16, 0.004, 2);
        bk.rotateZ(0.08);
        bk.translate(-0.08, 0.211 + 0.11, -0.2);   // i=0 선반(윗면 ~0.211)에 선다
        w.add(bk, 'redcloth', 0.5);
      });
    },
  },
  {
    id: 'shelf_cube', name: '큐브 선반', collection: 'manor', category: '서재', nofront: true,
    note: '정사각 칸이 격자로 짜인 낮은 선반. 그릇·책·상자를 칸마다.',
    dims: { cols: 3, rows: 2, cell: 0.36, t: 0.02, d: 0.34, wood: -1 },
    /* 왼쪽 아래 칸에 든 상자를 꺼내 본다 (2026-09-16) */
    openable: [{ part: '상자', slide: [0, 0, -0.2] }],
    build(w, d) {
      const W = woodOf(d, 'painted');
      const W_ = d.cols * d.cell + (d.cols + 1) * d.t, H = d.rows * d.cell + (d.rows + 1) * d.t;
      w.part('틀');
      for (let i = 0; i <= d.cols; i++) box(w, W, d.t, H, d.d, -W_ / 2 + d.t / 2 + i * (d.cell + d.t), H / 2, 0);
      for (let j = 0; j <= d.rows; j++) box(w, W, W_, d.t, d.d, 0, d.t / 2 + j * (d.cell + d.t), 0);
      w.part('뒤판'); box(w, W, W_, H, 0.008, 0, H / 2, d.d / 2 - 0.004);
      // 왼쪽 아래 칸에 든 잡동사니 상자 — 칸이 빈 것보다 뭐가 들어 있어야 선반이다
      w.movable('상자', () => {
        const bx = bevelBox(0.2, 0.2, 0.24, 0.008, 2);
        bx.translate(-W_ / 2 + d.t + d.cell / 2, d.t + 0.1, -0.02);
        w.add(bx, 'leather', 0.5);
      });
    },
  },
  {
    id: 'shelf_wall', name: '벽 선반', collection: 'manor', category: '서재', mount: 'wall', hangs: true, nofront: true,
    note: '벽에 붙이는 널 하나와 삼각 받침 둘. 원점은 붙인 지점(널 뒤 가운데).',
    dims: { w: 0.9, d: 0.22, t: 0.03, wood: -1 },
    /* 널 위에 올린 소품을 집어 든다 (2026-09-16) */
    openable: [{ part: '소품', slide: [0, 0, -0.12] }],
    build(w, d) {
      const W = woodOf(d, 'oak');
      w.part('널'); box(w, W, d.w, d.t, d.d, 0, 0, -d.d / 2, 0.004);
      w.part('받침'); for (const sx of [-1, 1]) { box(w, 'steel', 0.02, 0.16, 0.02, sx * (d.w / 2 - 0.12), -0.08 - d.t / 2, -0.01); box(w, 'steel', 0.02, 0.02, d.d - 0.04, sx * (d.w / 2 - 0.12), -d.t / 2 - 0.01, -d.d / 2 + 0.01); }
      // 널 끝에 올린 소품 — 빈 선반보다 뭐가 얹혀 있어야 쓰는 물건이다
      w.movable('소품', () => {
        const v = bevelBox(0.1, 0.12, 0.1, 0.012, 2);
        v.translate(0.24, d.t / 2 + 0.06, -d.d / 2);
        w.add(v, 'painted', 0.4);
      });
    },
  },
  // ---------- 침대 ----------
  {
    id: 'bed_platform', name: '플랫폼 침대', collection: 'manor', category: '침실', nofront: true,
    note: '헤드보드 없는 낮은 상자 틀 위에 매트리스. 젊은 사람 방.',
    dims: { w: 1.5, d: 2.0, frameH: 0.22, mattress: 0.2, wood: -1, cloth: -1 },
    /* 베개를 들어 올려 밑을 본다 (2026-09-16) */
    openable: [{ part: '베개', slide: [0, 0, -0.22] }],
    build(w, d) {
      const W = woodOf(d, 'oak'); const F = (['fabric', 'leather', 'redcloth'] as const)[d.cloth ?? -1] ?? 'fabric';
      w.part('틀'); box(w, W, d.w, d.frameH, d.d, 0, d.frameH / 2, 0, 0.008);
      w.part('매트리스'); const m = bevelBox(d.w - 0.1, d.mattress, d.d - 0.1, 0.04, 3); m.translate(0, d.frameH + d.mattress / 2, 0); w.add(m, F, 0.6);
      w.part('베개'); for (const sx of [-1, 1]) { const drawP = () => { const p = bevelBox(0.5, 0.12, 0.35, 0.04, 3); p.translate(sx * 0.32, d.frameH + d.mattress + 0.06, d.d / 2 - 0.3); w.add(p, F, 0.6); }; if (sx === -1) w.movable('베개', drawP); else drawP(); }
    },
  },
  {
    id: 'bed_metal', name: '철제 침대', collection: 'manor', category: '침실', nofront: true,
    note: '강관 프레임에 세로 살대 헤드·풋보드. 기숙사·관사 침대.',
    dims: { w: 1.0, d: 2.0, h: 0.5, headH: 1.0, footH: 0.75, tube: 0.025, cloth: -1 },
    /* 베개를 들어 올려 밑을 본다 (2026-09-16) */
    openable: [{ part: '베개', slide: [0, 0, -0.22] }],
    build(w, d) {
      const F = (['fabric', 'leather', 'redcloth'] as const)[d.cloth ?? -1] ?? 'fabric';
      const hw = d.w / 2 - d.tube / 2, hd = d.d / 2 - d.tube / 2;
      w.part('틀');
      for (const sx of [-1, 1]) { cyl(w, 'steel', d.tube / 2, d.headH, sx * hw, 0, hd, 10); cyl(w, 'steel', d.tube / 2, d.footH, sx * hw, 0, -hd, 10); box(w, 'steel', d.tube, d.tube, d.d, sx * hw, 0.3, 0); }
      for (const [z, hh] of [[hd, d.headH], [-hd, d.footH]] as const) {
        box(w, 'steel', d.w, d.tube, d.tube, 0, hh - d.tube / 2, z); box(w, 'steel', d.w, d.tube, d.tube, 0, 0.3, z);
        for (let i = 1; i < 6; i++) cyl(w, 'steel', 0.008, hh - 0.3 - d.tube, -hw + (d.w / 6) * i, 0.3, z, 8);
      }
      w.part('매트리스'); const m = bevelBox(d.w - 0.06, 0.18, d.d - 0.08, 0.03, 2); m.translate(0, 0.31 + 0.09, 0); w.add(m, F, 0.6);
      w.movable('베개', () => { const p = bevelBox(0.5, 0.11, 0.32, 0.04, 3); p.translate(0, 0.49 + 0.055, hd - 0.25); w.add(p, F, 0.6); });
    },
  },
  // ---------- 책상 ----------
  {
    id: 'desk_trestle', name: '트레슬 책상', collection: 'manor', category: '서재', nofront: true,
    note: '널판 하나를 A자 받침 둘에 얹은 작업 책상. 서랍이 없어 아래가 훤하다.',
    dims: { w: 1.6, d: 0.7, h: 0.75, topT: 0.035, wood: -1 },
    /* 책상을 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '책상', slide: [0, 0, -0.15] }],
    build(w, d) {
      w.movable('책상', () => {
      const W = woodOf(d, 'oak');
      w.part('상판'); box(w, W, d.w, d.topT, d.d, 0, d.h - d.topT / 2, 0, 0.005);
      w.part('받침');
      for (const sx of [-1, 1]) {
        const x = sx * (d.w / 2 - 0.22), top = d.h - d.topT;
        bar(w, W, new THREE.Vector3(x, 0.01, -d.d / 2 + 0.05), new THREE.Vector3(x, top, 0), 0.045);
        bar(w, W, new THREE.Vector3(x, 0.01, d.d / 2 - 0.05), new THREE.Vector3(x, top, 0), 0.045);
        box(w, W, 0.045, 0.04, d.d * 0.55, x, 0.32, 0);
      }
      });
    },
  },
  {
    id: 'desk_console', name: '콘솔 책상', collection: 'manor', category: '서재', nofront: true,
    note: '벽에 붙이는 얕고 긴 책상. 가는 각재 다리, 아래 선반 한 장. 노트북 하나 올릴 자리.',
    dims: { w: 1.2, d: 0.45, h: 0.76, topT: 0.03, legT: 0.035, wood: -1 },
    /* 책상을 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '책상', slide: [0, 0, -0.12] }],
    build(w, d) {
      w.movable('책상', () => {
      const W = woodOf(d, 'painted');
      w.part('상판'); box(w, W, d.w, d.topT, d.d, 0, d.h - d.topT / 2, 0, 0.004);
      w.part('다리'); for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(w, W, d.legT, d.h - d.topT, d.legT, sx * (d.w / 2 - d.legT / 2 - 0.02), (d.h - d.topT) / 2, sz * (d.d / 2 - d.legT / 2 - 0.02));
      w.part('선반'); box(w, W, d.w - 0.12, 0.02, d.d - 0.12, 0, 0.18, 0);
      });
    },
  },
  // ---------- 협탁·옷걸이 ----------
  {
    id: 'nightstand_round', name: '둥근 협탁', collection: 'manor', category: '침실', nofront: true,
    note: '작은 원판 둘을 다리 셋이 잇는 협탁. 아래 판에 책 한 권.',
    dims: { r: 0.22, h: 0.58, wood: -1 },
    /* 아래 판에 놓인 책을 집어 든다 (2026-09-16) */
    openable: [{ part: '책', slide: [0, 0, -0.16] }],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      w.part('판'); for (const y of [d.h - 0.015, 0.16]) { const g = new THREE.CylinderGeometry(d.r, d.r, 0.03, 28); g.translate(0, y, 0); w.add(g, W, 0.6); }
      w.part('다리'); for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; const g = bevelBox(0.03, d.h - 0.03, 0.03, 0.003); g.translate(0, (d.h - 0.03) / 2, d.r - 0.04); g.rotateY(a); w.add(g, W, 0.5); }
      // 아래 판에 놓인 책 — note 가 말하는 「책 한 권」, 집어 든다
      w.movable('책', () => {
        const bk = bevelBox(0.16, 0.035, 0.22, 0.006, 2);
        bk.rotateY(0.3);
        bk.translate(0.02, 0.16 + 0.015 + 0.018, -0.02);   // 아래 판(중심 0.16, 두께 0.03) 윗면
        w.add(bk, 'redcloth', 0.5);
      });
    },
  },
  {
    id: 'clothes_rail', name: '옷걸이 봉', collection: 'manor', category: '침실', nofront: true,
    note: '강관 봉에 옷 몇 벌이 걸린 이동식 행거. 옷장 없는 방.',
    dims: { w: 1.2, h: 1.6, d: 0.5, tube: 0.022, hangers: 6 },
    /* 걸린 옷 한 벌을 옷걸이째 들어 본다 (2026-09-16) */
    openable: [{ part: '옷', slide: [0, 0, -0.24] }],
    build(w, d) {
      w.part('틀');
      for (const sx of [-1, 1]) { cyl(w, 'steel', d.tube / 2, d.h, sx * (d.w / 2 - d.tube / 2), 0, 0, 10); box(w, 'steel', d.tube, d.tube, d.d, sx * (d.w / 2 - d.tube / 2), d.tube / 2, 0); }
      const rail = new THREE.CylinderGeometry(d.tube / 2, d.tube / 2, d.w, 10); rail.rotateZ(Math.PI / 2); rail.translate(0, d.h - d.tube / 2, 0); w.add(rail, 'steel', 0.3);
      w.part('옷');
      const cols: Slot[] = ['fabric', 'redcloth', 'leather', 'painted', 'fabric', 'ebony'];
      for (let i = 0; i < d.hangers; i++) {
        const x = -d.w / 2 + 0.15 + (i + 0.5) * ((d.w - 0.3) / d.hangers);
        const drawG = () => {
          const g = bevelBox(0.09, 0.9 + (i % 3) * 0.12, 0.34 - (i % 2) * 0.08, 0.02, 2); g.translate(x, d.h - 0.06 - (0.9 + (i % 3) * 0.12) / 2, 0); w.add(g, cols[i % cols.length], 0.6);
          cyl(w, 'steel', 0.004, 0.08, x, d.h - 0.08, 0, 6);
        };
        // 가운데 한 벌만 옷걸이째 들어 올릴 수 있게 뗀다
        if (i === 2) w.movable('옷', drawG); else drawG();
      }
    },
  },
];
